/**
 * Quais painéis pertencem a uma grade, e por quê isso muda onde eles aparecem.
 *
 * O mestre-detalhe liga uma grade a um ou mais PAINÉIS da própria tela: escolher
 * uma linha preenche os campos daquele painel. Isso já funciona — é o
 * `MasterDetailBinderService`.
 *
 * O que faltava é a APRESENTAÇÃO. A grade tem três modos, e dois deles dizem
 * onde o painel deve aparecer:
 *
 *  - `inline`  — o painel fica onde está na tela, e só é preenchido;
 *  - `drawer`  — o painel aparece numa gaveta lateral, ao escolher a linha;
 *  - `flip`    — a grade some e o painel toma o lugar dela.
 *
 * Nos dois últimos o painel **não pode continuar desenhado no lugar de origem**:
 * seriam duas cópias vivas dos mesmos controles, e o usuário editaria uma sem
 * ver a outra. Estas funções respondem quem são esses painéis.
 *
 * Puro de propósito: é regra sobre o documento, e a resposta tem de ser a mesma
 * para o `node-host` (que esconde) e para a grade (que desenha).
 */
import { ScreenDocument } from '../../model/screen-schema';
import { ScreenNode } from '../../model/node-props';

/** Os modos em que o painel muda de lugar. */
const MODES_THAT_MOVE_THE_PANEL: readonly string[] = ['drawer', 'flip'];

/**
 * Ids dos painéis que uma grade desenha em outro lugar.
 *
 * Conjunto vazio é o caso comum — tela sem grade, ou grade no modo `inline` —
 * e custa uma varredura da árvore, feita uma vez por documento.
 */
export function movedDetailPanelIds(document: ScreenDocument | null): ReadonlySet<string> {
  const ids = new Set<string>();

  if (document === null || document === undefined) {
    return ids;
  }

  visitNodes(document.root, node => {
    if (node.type !== 'datagrid') {
      return;
    }

    const props = node.props as {
      viewMode?: string;
      masterDetail?: { enabled?: boolean; panelIds?: readonly string[] };
    };

    if (props.masterDetail?.enabled !== true) {
      return;
    }
    if (!MODES_THAT_MOVE_THE_PANEL.includes(props.viewMode ?? 'inline')) {
      return;
    }

    for (const panelId of props.masterDetail.panelIds ?? []) {
      ids.add(panelId);
    }
  });

  return ids;
}

/**
 * Os nós dos painéis pedidos, na ordem em que foram pedidos.
 *
 * Id que não existe mais é simplesmente ignorado: o painel pode ter sido
 * removido depois de configurado, e estourar aqui derrubaria a tela inteira por
 * causa de uma referência órfã. Quem denuncia a órfã é a tela "Verificar tela".
 */
export function findPanels(
  document: ScreenDocument | null,
  panelIds: readonly string[]
): readonly ScreenNode[] {

  if (document === null || document === undefined || panelIds.length === 0) {
    return [];
  }

  const byId = new Map<string, ScreenNode>();

  visitNodes(document.root, node => byId.set(node.id, node));

  const found: ScreenNode[] = [];

  for (const panelId of panelIds) {
    const node = byId.get(panelId);

    if (node !== undefined) {
      found.push(node);
    }
  }

  return found;
}

function visitNodes(node: ScreenNode, visit: (node: ScreenNode) => void): void {
  visit(node);

  for (const child of node.children ?? []) {
    visitNodes(child, visit);
  }
}
